/**
 * Facts about the site and the company that appear in more than one place.
 *
 * Anything a lawyer, a registrar or an app store would care about lives here
 * rather than being typed into each page, so there is exactly one line to
 * change when the company address or the support address changes.
 */

/*
 * The address that actually serves the site, and therefore what canonical
 * tags, hreflang and Open Graph URLs must point at.
 *
 * www, not the bare domain, because rickey.app redirects to it — the same
 * convention as google.com and buildpassing.com. A canonical URL that
 * redirects wastes a hop and muddies the signal.
 *
 * PRODUCT.domain below stays bare: that one is read by humans, not machines.
 */
export { SITE_ORIGIN } from '../../shared/seo';

export const PRODUCT = {
  /*
   * The wordmark is always lowercase — "rickey", never "Rickey" as a logo and
   * never in caps. In running prose it takes an initial capital like any other
   * name, which is what this string is for.
   */
  name: 'Rickey',
  wordmark: 'rickey',
  domain: 'rickey.app',
  instagram: 'rickey.app',
  instagramUrl: 'https://instagram.com/rickey.app',

  /**
   * Where the app actually is, and therefore what every call to action does.
   *
   * Rickey went live on the App Store on 15 September 2026. Before that every
   * CTA opened the waitlist modal; now they are links to the store.
   *
   * ── Why this is a switch and not a deletion ────────────────────────────
   * Android is on hold, not cancelled. The waitlist — its modal, its provider,
   * its server route and its two emails — still works and is still wired; it
   * is simply not what the buttons do while `ios` is set. When there is an
   * Android release to announce, or anything else worth a one-message list,
   * this is the line that brings it back rather than a feature to rebuild.
   *
   * `android` stays null deliberately. The closed test exists, but a Play
   * link that resolves to nothing, or copy promising a store the app is not
   * on, is worse than silence.
   */
  store: {
    ios: 'https://apps.apple.com/app/id6810076356',
    android: null,
  },

  /**
   * The Product Hunt launch, while it is worth pointing at.
   *
   * Launched 17 September 2026. The badge in the hero renders only while this
   * is set; a week or two after launch, null removes it and nothing else has
   * to change. The image is self-hosted (public/producthunt.svg) with the vote
   * count stripped: PH's live badge would need img-src opened to a third
   * party and would send every visitor's IP to Product Hunt, and a hosted
   * copy of a live counter would read "0" forever.
   */
  productHunt:
    'https://www.producthunt.com/products/rickey?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-rickey' as
      string | null,
} as const;

/** True once the app can be downloaded somewhere. */
export const isReleased = PRODUCT.store.ios !== null || PRODUCT.store.android !== null;

export const COMPANY = {
  /**
   * The name that answers for this product in law.
   *
   * This does NOT have to be a registered company. If you operate as an
   * individual, put your own full legal name here — a sole trader is a
   * perfectly valid data controller, and both app stores issue individual
   * developer accounts. "BuildPassing" on its own is a trading name, which is
   * fine on the site but is not what a store or a regulator will match against.
   *
   * Before submitting to either app store this must be the exact name on the
   * developer account. See docs/legal.html.
   *
   * Set to the personal name on 9 September 2026, when Rickey's App Store
   * record was created: Apple team 3K4NZ5WBCU is an INDIVIDUAL enrolment, so
   * the listing shows "Alejandro Garza" as the seller and there is no
   * registered entity called BuildPassing for a regulator to match. The trade
   * name below is unchanged and is what every human-facing string still says.
   */
  legalName: 'Alejandro Garza',

  /** The name shown in ordinary copy — the footer, the emails, the marketing. */
  tradeName: 'BuildPassing',

  /**
   * Postal address of whoever is responsible for the data.
   *
   * Deliberately EMPTY until there is a real one. The legal pages check for
   * that and fall back to the email contact rather than printing a placeholder
   * — a made-up address in a privacy notice is worse than no address, because
   * it looks like a considered answer.
   *
   * When you do need one:
   *   - Mexican data protection law expects the responsable's domicilio in the
   *     aviso de privacidad.
   *   - Both app stores require an address on the developer account, and for
   *     EU distribution they publish it on the store listing under the Digital
   *     Services Act trader rules.
   * Neither requires a company — a personal or fiscal address is enough. See
   * docs/legal.html for the options.
   */
  address: '',

  /** Where the company is established. Used for the governing-law clause. */
  country: 'Mexico',

  domain: 'buildpassing.com',
  url: 'https://buildpassing.com',
  email: 'hola@buildpassing.com',
  privacyEmail: 'hola@buildpassing.com',
  supportEmail: 'hola@buildpassing.com',
} as const;

/** True once a real postal address has been filled in above. */
export const hasPostalAddress = (): boolean => COMPANY.address.trim().length > 0;

/**
 * The minimum age to hold an account.
 *
 * Higher than the other BuildPassing products, and deliberately: this app is
 * about alcoholic drinks, so it is rated 17+/18+ on the stores and the site
 * says so. See docs/legal.html.
 */
export const MINIMUM_AGE = 18;

/**
 * The date the current legal texts took effect. Update it whenever a legal page
 * changes materially — the privacy notice promises we will.
 */
export const LEGAL_LAST_UPDATED = '2026-08-25';
